'use client';

import { cn } from '../utils/cn';
import { Slider } from './slider';

export type Scale0To10FieldProps = {
  value?: number;
  onValueChange?: (value: number) => void;
  disabled?: boolean;
  lowLabel: string;
  highLabel: string;
  id?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  className?: string;
  /** Disabled preview — shows a sample position instead of an empty state. */
  preview?: boolean;
};

const PREVIEW_VALUE = 7;

export function Scale0To10Field({
  value,
  onValueChange,
  disabled = false,
  lowLabel,
  highLabel,
  id,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  className,
  preview = false,
}: Scale0To10FieldProps) {
  const hasValue = typeof value === 'number';
  const sliderValue = preview ? PREVIEW_VALUE : hasValue ? value : 0;
  const valueId = id ? `${id}-value` : undefined;
  const describedBy =
    [valueId, ariaDescribedBy].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('space-y-3', className)}>
      <output
        id={valueId}
        htmlFor={id}
        className={cn(
          'block tabular-nums text-2xl font-semibold tracking-tight text-foreground',
          !hasValue && !preview && 'text-muted-foreground',
          ariaInvalid && 'text-destructive',
        )}
        aria-live="polite"
      >
        {hasValue ? value : preview ? PREVIEW_VALUE : '—'}
      </output>

      <Slider
        id={id}
        min={0}
        max={10}
        step={1}
        disabled={disabled}
        value={[sliderValue]}
        onValueChange={(vals) => {
          const next = vals[0];
          if (next !== undefined) onValueChange?.(next);
        }}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid || undefined}
        aria-valuemin={0}
        aria-valuemax={10}
        aria-valuenow={hasValue ? value : preview ? PREVIEW_VALUE : undefined}
        aria-describedby={describedBy}
        className={cn(
          'py-2.5 touch-pan-y',
          '[&_[role=slider]]:size-6 sm:[&_[role=slider]]:size-5',
          !hasValue &&
            !preview &&
            '[&_.bg-primary]:opacity-0 [&_[role=slider]]:border-dashed [&_[role=slider]]:bg-muted',
          ariaInvalid &&
            '[&_.bg-primary]:bg-destructive [&_[role=slider]]:border-destructive',
          preview && 'opacity-80',
        )}
      />

      <div className="flex min-w-0 justify-between gap-3 text-xs leading-snug text-muted-foreground">
        <span className="min-w-0 break-words">{lowLabel}</span>
        <span className="min-w-0 text-end break-words">{highLabel}</span>
      </div>
    </div>
  );
}
